import { applyEach, required, schema, validate } from '@angular/forms/signals';

import { DictionaryDto } from '@app/shared/services/dtos/dictionary.dto';
import { DangerousSituationDto } from '@app/shared/services/dtos/osh/dangerous-situation.dto';
import { CreateOshDangerousSituation, Osh } from '@app/shared/types/models/osh/osh.model';

export enum ConsequencesEnum {
  VeryLow = 'VeryLow',
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  VeryHigh = 'VeryHigh'
}

export enum ProbabilityEnum {
  Rare = 'Rare',
  Unlikely = 'Unlikely',
  Possible = 'Possible',
  Likely = 'Likely',
  AlmostCertain = 'AlmostCertain'
}

const REQUIRED_MESSAGE = 'ui-forms-this-field-is-required';

/** OSH risk matrix colors — SSOT in proffeo-tokens.css. */
const OSH_RISK_COLOR = {
  level1: 'var(--proffeo-color-osh-risk-medium)',
  level2: 'var(--proffeo-color-osh-risk-low)',
  level3: 'var(--proffeo-color-osh-risk-high)',
  level4: 'var(--proffeo-color-osh-risk-critical)'
} as const;

/** Plain form model of the OSH analysis dialog (assigned users live outside: they are `User` class instances). */
export interface OshAnalysisFormModel {
  workDescription: string;
  workStartDate: Date | null;
  workType: DictionaryDto | null;
  workPossibleThreat: DictionaryDto | null;
  dangerousSituations: DangerousSituationFormModel[];
}

export interface DangerousSituationFormModel {
  /** `''` for a situation that has not been saved yet. */
  id: string;
  possibleThreat: DictionaryDto | null;
  probability: ProbabilityEnum | null;
  consequences: ConsequencesEnum | null;
  remedies: DictionaryDto[];
  secondaryRiskAnalysis: SecondaryRiskAnalysisFormModel;
}

export interface SecondaryRiskAnalysisFormModel {
  probability: ProbabilityEnum | null;
  consequences: ConsequencesEnum | null;
}

export interface OshRiskLevel {
  color: string;
  riskLevel: number | null;
  description: string | null;
}

/** Plain copy of a dictionary entry (store entries are `Dictionary` class instances). */
export function toDictionaryDto(dictionary: DictionaryDto | null | undefined): DictionaryDto | null {
  return dictionary ? { ...dictionary } : null;
}

export function createDangerousSituation(value?: DangerousSituationDto): DangerousSituationFormModel {
  return {
    id: value?.id ?? '',
    possibleThreat: toDictionaryDto(value?.possibleThreat),
    probability: value?.probability ?? null,
    consequences: value?.consequences ?? null,
    remedies: (value?.remedies ?? []).map(remedy => ({ ...remedy })),
    secondaryRiskAnalysis: {
      probability: value?.secondaryProbability ?? null,
      consequences: value?.secondaryConsequences ?? null
    }
  };
}

export function createOshAnalysisModel(oshAnalysis?: Osh): OshAnalysisFormModel {
  if (!oshAnalysis) {
    return {
      workDescription: '',
      workStartDate: null,
      workType: null,
      workPossibleThreat: null,
      dangerousSituations: [createDangerousSituation()]
    };
  }

  return {
    workDescription: oshAnalysis.workDescription ?? '',
    workStartDate: oshAnalysis.workStartDate ? new Date(oshAnalysis.workStartDate) : null,
    workType: toDictionaryDto(oshAnalysis.workType),
    workPossibleThreat: toDictionaryDto(oshAnalysis.workPossibleThreat),
    dangerousSituations: (oshAnalysis.dangerousSituations ?? []).map(situation => createDangerousSituation(situation))
  };
}

export const oshAnalysisSchema = schema<OshAnalysisFormModel>(path => {
  required(path.workDescription, { message: REQUIRED_MESSAGE });
  required(path.workStartDate, { message: REQUIRED_MESSAGE });
  required(path.workType, { message: REQUIRED_MESSAGE });
  required(path.workPossibleThreat, { message: REQUIRED_MESSAGE });
  applyEach(path.dangerousSituations, situation => {
    required(situation.possibleThreat, { message: REQUIRED_MESSAGE });
    // `required()` treats an empty array as a value; at least one remedy has to be picked.
    validate(situation.remedies, ({ value }) =>
      value().length === 0 ? { kind: 'required', message: REQUIRED_MESSAGE } : null
    );
  });
});

/** Risk level is always derived from probability × consequences (never stored in the form model). */
export function calculateRiskLevel(
  probability: ProbabilityEnum | null,
  consequence: ConsequencesEnum | null
): OshRiskLevel {
  if (
    consequence === ConsequencesEnum.VeryLow &&
    (probability === ProbabilityEnum.Unlikely ||
      probability === ProbabilityEnum.Possible ||
      probability === ProbabilityEnum.Rare)
  ) {
    return { color: OSH_RISK_COLOR.level1, riskLevel: 1, description: 'osh.risk-first-level-description' };
  }
  if (
    consequence === ConsequencesEnum.VeryLow &&
    (probability === ProbabilityEnum.AlmostCertain || probability === ProbabilityEnum.Likely)
  ) {
    return { color: OSH_RISK_COLOR.level2, riskLevel: 2, description: 'osh.risk-second-level-description' };
  }
  if (consequence === ConsequencesEnum.Low && probability === ProbabilityEnum.Rare) {
    return { color: OSH_RISK_COLOR.level1, riskLevel: 1, description: 'osh.risk-first-level-description' };
  }
  if (
    consequence === ConsequencesEnum.Low &&
    (probability === ProbabilityEnum.Unlikely || probability === ProbabilityEnum.Possible)
  ) {
    return { color: OSH_RISK_COLOR.level2, riskLevel: 2, description: 'osh.risk-second-level-description' };
  }
  if (
    consequence === ConsequencesEnum.Low &&
    (probability === ProbabilityEnum.AlmostCertain || probability === ProbabilityEnum.Likely)
  ) {
    return { color: OSH_RISK_COLOR.level3, riskLevel: 3, description: 'osh.risk-third-level-description' };
  }
  if (
    consequence === ConsequencesEnum.Medium &&
    (probability === ProbabilityEnum.Rare || probability === ProbabilityEnum.Unlikely)
  ) {
    return { color: OSH_RISK_COLOR.level2, riskLevel: 2, description: 'osh.risk-second-level-description' };
  }
  if (
    consequence === ConsequencesEnum.Medium &&
    (probability === ProbabilityEnum.Possible || probability === ProbabilityEnum.Likely)
  ) {
    return { color: OSH_RISK_COLOR.level3, riskLevel: 3, description: 'osh.risk-third-level-description' };
  }
  if (consequence === ConsequencesEnum.Medium && probability === ProbabilityEnum.AlmostCertain) {
    return { color: OSH_RISK_COLOR.level4, riskLevel: 4, description: 'osh.risk-fourth-level-description' };
  }
  if (
    consequence === ConsequencesEnum.High &&
    (probability === ProbabilityEnum.Unlikely ||
      probability === ProbabilityEnum.Possible ||
      probability === ProbabilityEnum.Rare)
  ) {
    return { color: OSH_RISK_COLOR.level3, riskLevel: 3, description: 'osh.risk-third-level-description' };
  }
  if (
    consequence === ConsequencesEnum.High &&
    (probability === ProbabilityEnum.AlmostCertain || probability === ProbabilityEnum.Likely)
  ) {
    return { color: OSH_RISK_COLOR.level4, riskLevel: 4, description: 'osh.risk-fourth-level-description' };
  }
  if (
    consequence === ConsequencesEnum.VeryHigh &&
    (probability === ProbabilityEnum.Unlikely || probability === ProbabilityEnum.Rare)
  ) {
    return { color: OSH_RISK_COLOR.level3, riskLevel: 3, description: 'osh.risk-third-level-description' };
  }
  if (
    consequence === ConsequencesEnum.VeryHigh &&
    (probability === ProbabilityEnum.Likely ||
      probability === ProbabilityEnum.Possible ||
      probability === ProbabilityEnum.AlmostCertain)
  ) {
    return { color: OSH_RISK_COLOR.level4, riskLevel: 4, description: 'osh.risk-fourth-level-description' };
  }
  return { color: 'rgba(255,255,255,0)', riskLevel: null, description: null };
}

/** Risk level as sent to the API (`string` per the DTO); `undefined` (omitted) while it cannot be derived. */
function toRiskLevelRequest(probability: ProbabilityEnum | null, consequences: ConsequencesEnum | null): string {
  const { riskLevel } = calculateRiskLevel(probability, consequences);
  return riskLevel === null ? undefined : String(riskLevel);
}

export function toDangerousSituationRequest(
  situation: DangerousSituationFormModel
): CreateOshDangerousSituation &
  Pick<DangerousSituationDto, 'secondaryProbability' | 'secondaryConsequences' | 'secondaryRiskLevel'> {
  const { probability, consequences } = situation.secondaryRiskAnalysis;
  return {
    possibleThreat: situation.possibleThreat,
    id: situation.id || undefined,
    probability: situation.probability ?? undefined,
    consequences: situation.consequences ?? undefined,
    riskLevel: toRiskLevelRequest(situation.probability, situation.consequences),
    remedies: situation.remedies,
    secondaryProbability: probability ?? undefined,
    secondaryConsequences: consequences ?? undefined,
    secondaryRiskLevel: toRiskLevelRequest(probability, consequences)
  };
}
